import React, { useEffect, useMemo, useRef } from 'react';
import { TextVariant } from '@naf/text';
import { useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import useSelector from '@redux/typedHooks';
import { useStateMachine } from 'little-state-machine';
import { HolderFormType } from '@appTypes/internationalDrivingPermitFormType';
import { ProductID } from '@lib/ProductID';
import { actions as idpActions } from '@redux/modules/internationalDrivingPermit';
import { INTERNATIONAL_DRIVING_PERMIT_FORM_NAME } from '../../../lib/constants';
import { updateAction } from '../../../lib/updateAction';
import { MembershipBox, NoMembershipBox } from './MembershipBox/MembershipBox';
import { createMembershipOption, IFK_CAMPAIGN_CODE, MembershipBundleProps } from './MembershipBundle.utils';
import * as S from './MembershipBundle.styles';

const NO_MEMBERSHIP_OPTION = 'no-membership';

export const MembershipBundle = ({ membershipProductData }: MembershipBundleProps) => {
  const dispatch = useDispatch();
  const {
    actions,
    state: { internationalDrivingPermitForm },
  } = useStateMachine({ updateAction });
  const products = useSelector((state) => state.internationalDrivingPermit.data.eligibleProducts);
  const {
    register,
    setValue,
    watch,
    formState: { errors },
  } = useFormContext<HolderFormType>();

  const storedSelectedOption =
    internationalDrivingPermitForm?.membership === null
      ? NO_MEMBERSHIP_OPTION
      : internationalDrivingPermitForm?.membership?.productId || '';
  const requestedCampaignCodeRef = useRef<string | undefined>(
    internationalDrivingPermitForm?.membership?.productId ? IFK_CAMPAIGN_CODE : undefined,
  );
  const selectedOption = watch('membershipSelection') || storedSelectedOption;
  const membershipSelectErrorMessage = errors.membershipSelection?.message;

  useEffect(() => {
    register('membershipSelection');
  }, [register]);

  useEffect(() => {
    setValue('membershipSelection', storedSelectedOption, { shouldValidate: false });
  }, [setValue, storedSelectedOption]);

  const availableMembershipOptions = useMemo(() => {
    if (!membershipProductData?.memberships) {
      return [];
    }

    return (
      membershipProductData.memberships
        // We only offer these memberships in this funnel.
        .filter(
          (membership) =>
            membership.productId === ProductID.MembershipWithRoadsideAssistance ||
            membership.productId === ProductID.Membership,
        )
        .map((membership) => createMembershipOption(membership, products))
    );
  }, [membershipProductData, products]);

  const handleSelectOption = (id: string) => {
    const hasSelectedMembership = id !== NO_MEMBERSHIP_OPTION;
    const campaignCode = hasSelectedMembership ? IFK_CAMPAIGN_CODE : undefined;

    setValue('membershipSelection', id, { shouldDirty: true, shouldTouch: true, shouldValidate: true });

    if (requestedCampaignCodeRef.current !== campaignCode) {
      requestedCampaignCodeRef.current = campaignCode;
      dispatch(idpActions.getInternationalDrivingPermit.request(campaignCode));
    }

    actions.updateAction({
      formName: INTERNATIONAL_DRIVING_PERMIT_FORM_NAME,
      data: {
        ...internationalDrivingPermitForm,
        membership:
          id === NO_MEMBERSHIP_OPTION
            ? null
            : {
                productId: id,
                amount: '1',
              },
      },
    });
  };

  return (
    <S.BundleWrapper>
      <S.Title variant={TextVariant.Header2}>Bli NAF-medlem og få internasjonalt førerkort i velkomstgave</S.Title>
      <S.MembershipBoxWrapper>
        {availableMembershipOptions.map((option) => (
          <MembershipBox
            key={option.id}
            id={option.id}
            title={option.title}
            price={option.price}
            hasBorder={option.hasBorder}
            showTag={option.showTag}
            benefitAccordions={option.benefitAccordions}
            selected={selectedOption === option.id}
            onSelect={handleSelectOption}
          />
        ))}
        <NoMembershipBox
          id={NO_MEMBERSHIP_OPTION}
          title="Nei takk, jeg ønsker ikke å bli NAF-medlem nå. Jeg betaler derfor full pris for Internasjonalt førerkort."
          selected={selectedOption === NO_MEMBERSHIP_OPTION}
          onSelect={handleSelectOption}
        />
        {membershipSelectErrorMessage && <S.StyledMessage error>{membershipSelectErrorMessage}</S.StyledMessage>}
      </S.MembershipBoxWrapper>
    </S.BundleWrapper>
  );
};
